import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { downloadBlob } from '@sequencehq/utils'
import SpinnerContainer from 'components/Loading/SpinnerContainer'

const JOURNAL_REPORTS_PATH = '/revenue-recognition/journal-reports'
const DEFAULT_FILENAME = 'journal-report.csv'

const JOURNAL_REPORT_FILENAME_PATTERN =
  /^(\w+_\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2})_.+\.csv$/

export const getFilenameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname
    const filename = pathname.split('/').pop() ?? ''
    const match = filename.match(JOURNAL_REPORT_FILENAME_PATTERN)
    if (!match) {
      Sentry.captureException(
        new Error(
          `Journal report filename did not match expected pattern: ${filename}`
        )
      )
      return DEFAULT_FILENAME
    }
    return `${match[1]}.csv`
  } catch (error) {
    Sentry.captureException(error)
    return DEFAULT_FILENAME
  }
}

export const JournalReportDownload = () => {
  const [searchParams] = useSearchParams()
  const signature = searchParams.get('signature')
  const navigate = useNavigate()
  const { displayNotification } = useNotifications()

  const { data, isError, isSuccess } = useQuery(
    dashboard20240730Client.getJournalReportSignedDownload,
    { signature: signature ?? '' },
    { enabled: !!signature }
  )

  useEffect(() => {
    if (isSuccess && data?.url) {
      ;(async () => {
        try {
          const response = await fetch(data.url)
          if (!response.ok) {
            throw new Error('Download failed')
          }

          const filename = getFilenameFromUrl(data.url)
          const blob = await response.blob()
          downloadBlob(filename)(blob)

          displayNotification('Downloaded successfully', {
            type: 'success'
          })
        } catch {
          displayNotification('Failed to download report', {
            type: 'error'
          })
        } finally {
          navigate(JOURNAL_REPORTS_PATH)
        }
      })()
    } else if (isError || !signature) {
      displayNotification('This download link has expired or is invalid', {
        type: 'error'
      })
      navigate(JOURNAL_REPORTS_PATH)
    }
  }, [signature, isSuccess, isError, data, displayNotification, navigate])

  return <SpinnerContainer />
}
